import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

const LeftArrow = 37;
const RightArrow = 39;
const Tab = 9;
const Enter = 13;

type Props = {
  defaultFocusIndex: number,
  buttons: Array<string>,
  onButton: (x:string) => void,
  customClass?: string|undefined,
  addOr?: boolean,
  addSpace?: boolean,
};

/**
 * ButtonGroup - inline list of buttons 
 * 
 * Props are:
 * 
 *  buttons: array of strings representing button labels. The buttons are rendered in the order of elements in this list
 *  defaultFocusIndex: the index of button which has focus by default
 *  onButton: event handler for buttons. Ir receives string argument of label of the button being clicked on. The event handler is also invoked on Enter key
 *
 * @param {*} { defaultFocusIndex = 0, buttons, onButton }
 * @return {*} 
 */
const ButtonGroup: React.FC<Props> = ({ defaultFocusIndex = 0, buttons, onButton, customClass = undefined, addOr = false, addSpace = false }) => {
  const buttonRefs = useRef<Array<HTMLButtonElement|null>>([]);
  const [enterScheduledFor, setEnterScheduledFor] = useState<string|null>(null);

  useEffect(() => {
    buttonRefs.current = buttonRefs.current.slice(0, buttons.length);
    if(defaultFocusIndex !== -1) buttonRefs.current[defaultFocusIndex]!.focus();
  }, [buttons, defaultFocusIndex]);

  useEffect(() => {
    if (!enterScheduledFor) {
      return;
    }

    onButton(enterScheduledFor);
    setEnterScheduledFor(null);
  }, [enterScheduledFor, onButton]);

  function focusNext(index: number) {
    const focusIndex = index === buttons.length - 1 ? 0 : index + 1;
    buttonRefs.current[focusIndex]!.focus();
  }

  function focusPrevious(index: number) {
    const focusIndex = index === 0 ? buttons.length - 1 : index - 1;
    buttonRefs.current[focusIndex]!.focus();
  }

  function onButtonKeyDown(e:React.KeyboardEvent<HTMLElement>) {
    const index = buttonRefs.current.findIndex(x => x === e.target);

    if (index < 0 || index >= buttons.length) {
      // Should never happen
      return;
    }

    switch (e.keyCode) {
      case Tab:
        if (e.shiftKey) {
          focusPrevious(index);
        } else {
          focusNext(index);
        }
        e.stopPropagation();
        e.preventDefault();
        break;
      case LeftArrow:
        focusPrevious(index);
        e.stopPropagation();
        e.preventDefault();
        break;
      case RightArrow:
        focusNext(index);
        e.stopPropagation();
        e.preventDefault();
      break;
      case Enter:
        setEnterScheduledFor(buttons[index]);
        e.stopPropagation();
        e.preventDefault();
        break;
    default:
      break;
    }
  }

  const classNames = customClass ? `${customClass} ifms-primary-inverse-button ifms-focusable-inverse ` : 'ifms-primary-inverse-button ifms-focusable-inverse';

  return (
    <>
    <div className="float-right" style={{display:'flex'}} >
      {buttons.map((b, i) =>(
        <div key={`ifms-dialog-button-div-${i}`} style={{display:'flex'}}>
        <button
          name={`ifms-dialog-button-${b}`}
          key={`ifms-dialog-button-${i}`}
          ref={el => buttonRefs.current[i] = el} 
          className={classNames}
          onClick={() => onButton(b)}
          onKeyDown={onButtonKeyDown}
        >
          {b}
        </button>
        {addOr && i%2 === 0 && <div style={{marginLeft:'4px',marginRight:'4px', marginTop:'8px', fontSize:'14px' }}> or </div>} 
        {addSpace && i%2 === 0 && <div style={{marginLeft:'4px',marginRight:'4px', marginTop:'8px', fontSize:'14px' }}> &nbsp; </div>} 
        </div>
      ))}
    </div>
    <div className="clearfix" ></div>
    </>
  );
};

ButtonGroup.defaultProps = {
  onButton: () => {},
  defaultFocusIndex: 0,
};

ButtonGroup.propTypes = {
  onButton: PropTypes.func.isRequired,
  buttons: PropTypes.array.isRequired,
  defaultFocusIndex: PropTypes.number.isRequired,
  customClass: PropTypes.string,
};

export default React.memo(ButtonGroup);